import React from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { MessageKey } from '../i18n/messages';
import { TargetTimeInput, TargetTimeMode } from '../web-api-client';

interface Props {
  value: TargetTimeInput;
  onChange: (value: TargetTimeInput) => void;
}

const MODE_LABEL_KEY: Record<TargetTimeMode, MessageKey> = {
  [TargetTimeMode.None]: 'targetTime.none',
  [TargetTimeMode.ExactDateTime]: 'targetTime.exact',
  [TargetTimeMode.TimeOfDay]: 'targetTime.timeOfDay',
  [TargetTimeMode.Duration]: 'targetTime.duration'
};

/** "2026-08-29T17:00" từ ô datetime-local sang mốc tuyệt đối theo múi giờ máy người dùng. */
function localInputToInstant(value: string): string | undefined {
  if (!value) return undefined;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function instantToLocalInput(instant: string | undefined): string {
  if (!instant) return '';

  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (n: number) => (n < 10 ? '0' + n : String(n));

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * Ba cách nhập thời gian mục tiêu. Giao diện chỉ thu thập đúng thứ người dùng chọn rồi gửi nguyên
 * dạng đó lên; việc quy về một mốc duy nhất là của máy chủ, vì nó mới biết ngày của todo và múi giờ
 * đã đăng ký của tài khoản.
 */
export function TargetTimeFields({ value, onChange }: Props) {
  const { t } = useI18n();
  const mode = value.mode ?? TargetTimeMode.None;

  const changeMode = (next: TargetTimeMode) => {
    // Đổi cách nhập thì bỏ hết giá trị của cách cũ, tránh gửi lên hai trường cùng lúc.
    onChange(new TargetTimeInput({ mode: next }));
  };

  return (
    <div className="row g-2 align-items-end">
      <div className="col-sm-5">
        <label className="form-label small text-body-secondary">{t('targetTime.label')}</label>
        <select
          className="form-select"
          value={mode}
          onChange={event => changeMode(event.target.value as TargetTimeMode)}
        >
          {Object.values(TargetTimeMode).map(item => (
            <option key={item} value={item}>
              {t(MODE_LABEL_KEY[item])}
            </option>
          ))}
        </select>
      </div>

      {mode === TargetTimeMode.ExactDateTime && (
        <div className="col-sm-7">
          <input
            type="datetime-local"
            className="form-control"
            value={instantToLocalInput(value.at)}
            onChange={event =>
              onChange(new TargetTimeInput({ mode, at: localInputToInstant(event.target.value) }))
            }
          />
        </div>
      )}

      {mode === TargetTimeMode.TimeOfDay && (
        <div className="col-sm-7">
          <input
            type="time"
            className="form-control"
            value={(value.timeOfDay ?? '').slice(0, 5)}
            onChange={event =>
              onChange(
                new TargetTimeInput({
                  mode,
                  // TimeOnly ở máy chủ cần đủ cả giây.
                  timeOfDay: event.target.value ? `${event.target.value}:00` : undefined
                })
              )
            }
          />
          <div className="form-text">{t('targetTime.timeOfDayHint')}</div>
        </div>
      )}

      {mode === TargetTimeMode.Duration && (
        <div className="col-sm-7">
          <div className="input-group">
            <input
              type="number"
              min={1}
              className="form-control"
              value={value.durationMinutes ?? ''}
              onChange={event =>
                onChange(
                  new TargetTimeInput({
                    mode,
                    durationMinutes: event.target.value ? Number(event.target.value) : undefined
                  })
                )
              }
            />
            <span className="input-group-text">{t('targetTime.minutes')}</span>
          </div>
          <div className="form-text">{t('targetTime.durationHint')}</div>
        </div>
      )}
    </div>
  );
}
